import type { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import type { TLocalize } from '@hooks/use-localize';
import type { INowFormValues, INowModel } from '@/types/now';
import {
    AddressLine1Field,
    CityField,
    CountryOfResidenceField,
    DonationTypeField,
    DonorMentorOptInField,
    EmailField,
    FirstNameField,
    HipaaField,
    InternationalPhoneField,
    LastNameField,
    MobilePhoneField,
    PostalCodeField,
    ResidenceSection,
    ResidesInUsaField,
    StateField,
    TextOptInNotice,
} from '@views/register/now-sections/now-fields';

interface IProps {
    model: INowModel;
    t: TLocalize;
    onSubmit: React.FormEventHandler<HTMLFormElement>;
    isSubmitting: boolean;
    submitError: string | null;
    alerts: ReactNode;
}

const StandardNow = ({ model, t, onSubmit, isSubmitting, submitError, alerts }: IProps) => {
    const isResidingInUsa = useFormContext<INowFormValues>().watch('isResidingInUsa');

    const introParagraph = model.isLiverMicrosite
        ? t(
              'NowIntro_Paragraph_1_Liver',
              'The National Liver Registry offers several programs and protections to support individuals through the donation process, making the donation experience as safe, convenient and positive as possible',
          )
        : t(
              'NowIntro_Paragraph_1',
              'The National Kidney Registry offers several programs and protections to support individuals through the donation process, making the donation experience as safe, convenient and positive as possible',
          );

    return (
        <div className="row">
            {alerts}

            {!model.isPrivateLabel && (
                <div className="col-xs-12 col-sm-6 col-md-3">
                    {model.micrositeRecipient?.firstName != null ? (
                        <h1>
                            {t('NowIntro_TitleOnBehalfOf', "See if you're qualified to donate on behalf of")}
                            <br />
                            <strong>
                                {model.micrositeRecipient.firstName} {model.micrositeRecipient.lastName}
                            </strong>
                            .
                        </h1>
                    ) : (
                        <h1>{t('NowIntro_Title', "See if you're qualified to donate")}.</h1>
                    )}

                    <p>{introParagraph}.</p>

                    <p>
                        {t(
                            'NowIntro_Paragraph_2',
                            'Providing your contact information or filling out the questionnaire does not obligate you to become a donor. You are free to opt out of the process at any time',
                        )}
                        .
                    </p>

                    <p>
                        <strong>
                            {t(
                                'NowIntro_Paragraph_3',
                                'Thank you for your interest in becoming a living donor and saving a life',
                            )}
                            !
                        </strong>
                    </p>
                </div>
            )}

            <div
                className={
                    model.isPrivateLabel || model.micrositeRecipient != null
                        ? 'col-xs-12 col-sm-8'
                        : 'col-xs-12 col-sm-6 col-md-5'
                }
            >
                <form id="RegistrationForm" className="form-horzontal" onSubmit={onSubmit}>
                    <FirstNameField t={t} />
                    <LastNameField t={t} />
                    <ResidesInUsaField t={t} />

                    <ResidenceSection id="non-us-residence" show={isResidingInUsa === '0'}>
                        <CountryOfResidenceField t={t} countries={model.countries} />
                        <InternationalPhoneField t={t} />
                    </ResidenceSection>

                    <ResidenceSection id="us-residence" show={isResidingInUsa === '1'}>
                        <AddressLine1Field t={t} />
                        <PostalCodeField t={t} />
                        <CityField t={t} />
                        <StateField t={t} states={model.states} />
                        <MobilePhoneField t={t} />
                        <TextOptInNotice t={t} />
                    </ResidenceSection>

                    <EmailField t={t} />

                    {model.isDashLiverAvailable && (
                        <DonationTypeField t={t} donationTypes={model.donationTypes} />
                    )}

                    <HipaaField t={t} />

                    <DonorMentorOptInField t={t} />
                </form>

                {submitError && <div className="alert alert-danger">{submitError}</div>}

                <button
                    className="btn btn-primary"
                    type="submit"
                    form="RegistrationForm"
                    disabled={isSubmitting}
                >
                    {t('Word_Continue', 'Continue')}
                </button>
            </div>
        </div>
    );
};

export default StandardNow;
